/**
 * Confidence Tracking Utility
 * 
 * Manages confidence level calculations for adaptive learning
 * Extracted from App.jsx to improve code organization
 */

import { RESPONSE_TIMES } from '../constants/timing';

/**
 * Calculate new confidence level based on answer correctness and response time
 * @param {Object} options - Confidence calculation options
 * @param {number} options.currentConfidence - Current confidence level (0-1)
 * @param {boolean} options.isCorrect - Whether the answer was correct
 * @param {number} options.responseTime - Response time in milliseconds
 * @returns {number} New confidence level (0-1)
 */
export const calculateConfidenceLevel = ({
  currentConfidence = 0.5,
  isCorrect,
  responseTime
}) => {
  let newConfidence = currentConfidence;
  
  // Adjust based on correctness
  if (isCorrect) {
    newConfidence += 0.1;
  } else {
    newConfidence -= 0.15;
  }
  
  // Adjust based on response time (faster = more confident)
  if (responseTime < RESPONSE_TIMES.FAST) {
    newConfidence += 0.05;
  } else if (responseTime > RESPONSE_TIMES.SLOW) {
    newConfidence -= 0.05;
  }
  
  // Clamp between 0 and 1
  return Math.max(0, Math.min(1, newConfidence));
};

/**
 * Update confidence level for a subject
 * @param {Object} options - Update options
 * @param {Object} options.confidenceLevels - Current confidence levels object
 * @param {string} options.subject - Subject key
 * @param {boolean} options.isCorrect - Whether the answer was correct
 * @param {number} options.responseTime - Response time in milliseconds
 * @returns {Object} Updated confidence levels object
 */
export const updateConfidenceLevel = ({
  confidenceLevels = {},
  subject,
  isCorrect,
  responseTime
}) => {
  const currentConfidence = confidenceLevels[subject] || 0.5;
  const newConfidence = calculateConfidenceLevel({
    currentConfidence,
    isCorrect,
    responseTime
  });
  
  return {
    ...confidenceLevels,
    [subject]: newConfidence
  };
};

export default {
  calculateConfidenceLevel,
  updateConfidenceLevel
};

